import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useCountUpDisplay } from "@/hooks/useCountUp";

export interface StatTileProps {
  label: string;
  value: string | number;
  /** Small supporting line under the numeral. */
  delta?: ReactNode;
  /** Tone applied to the delta chip. */
  tone?: "up" | "down" | "neutral";
  icon?: ReactNode;
  /** Categorical accent index (1-6) driving the icon tile + rim. */
  accent?: 1 | 2 | 3 | 4 | 5 | 6;
  footer?: ReactNode;
  className?: string;
  /** Animate the numeral counting up on mount. Default true. */
  animate?: boolean;
}

const ACCENT_BG: Record<number, string> = {
  1: "bg-cat-1/15 text-cat-1",
  2: "bg-cat-2/15 text-cat-2",
  3: "bg-cat-3/15 text-cat-3",
  4: "bg-cat-4/15 text-cat-4",
  5: "bg-cat-5/15 text-cat-5",
  6: "bg-cat-6/15 text-cat-6",
};

const TONE: Record<string, string> = {
  up: "text-success",
  down: "text-destructive",
  neutral: "text-muted-foreground",
};

export default function StatTile({
  label,
  value,
  delta,
  tone = "neutral",
  icon,
  accent = 1,
  footer,
  className,
  animate = true,
}: StatTileProps) {
  const animated = useCountUpDisplay(value);
  const shown = animate ? animated : String(value);

  return (
    <div className={cn("glass-card glow-hover p-5", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="kicker">{label}</p>
          <p className="metric-value mt-2">{shown}</p>
          {delta && (
            <p className={cn("mt-2 text-xs font-medium", TONE[tone])}>{delta}</p>
          )}
        </div>
        {icon && (
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
              ACCENT_BG[accent],
            )}
          >
            {icon}
          </div>
        )}
      </div>
      {footer && <div className="mt-4">{footer}</div>}
    </div>
  );
}
